import { Injectable } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SideDetailViewService } from '@y42/core/container-with-side-detail-view';
import { DataTestRunnerService, DataTestType, RowDriftTestForm, SchemaTestForm } from '@y42/data-test';
import { DatasourceColumnUIModel, DatasourceTableQuery } from '@y42/datasource';
import { ErrorHandlerService } from '@y42/error-handling';
import { UnwrapArray } from '@y42/typescript';
import { isNotNullish, isNullish } from '@y42/util/nullish';
import { takePromise } from '@y42/util/take-promise';
import { combineLatest, filter, firstValueFrom, Observable, of, throwError } from 'rxjs';
import { map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { DataTestDialogStateService } from '../components/data-test-dialog/data-test-dialog-state.service';
import { DataTestBuilderFormService } from './data-test-builder-form.service';
import {
  DataTestBuilderSaveOutput,
  DataTestBuilderSidebarInput,
  isEditDataTest,
  isNewDataTest,
} from './data-test-builder.model';

@UntilDestroy()
@Injectable()
export class DataTestBuilderService {
  constructor(
    private readonly dataTestBuilderFormService: DataTestBuilderFormService,
    private readonly dataTestDialogStateService: DataTestDialogStateService,
    private readonly datasourceTableQuery: DatasourceTableQuery,
    private readonly sideDetailViewService: SideDetailViewService<
      DataTestBuilderSaveOutput,
      DataTestBuilderSidebarInput
    >,
    private readonly dataTestTestRunnerService: DataTestRunnerService,
    private readonly errorHandlerService: ErrorHandlerService,
  ) {
    this.disableDataTestTypeDropdownListOnCondition();
    this.initMainFormOnSidebarOpen();
  }

  private readonly data$: Observable<DataTestBuilderSidebarInput> = this.sideDetailViewService.sidenavOpened$.pipe(
    filter((isOpened) => isOpened),
    withLatestFrom(this.sideDetailViewService.data$),
    switchMap(([, data]) =>
      isNotNullish(data) ? of(data) : throwError(() => new Error('[Data-test] sidebar input parameter is null')),
    ),
  );

  private readonly schemaTestExists$: Observable<boolean> = this.data$.pipe(
    map((data) => isNotNullish(data.dataTests.find((x) => x.type === DataTestType.Schema))),
    untilDestroyed(this),
  );

  /**
   * defines whether a certain data test type is available for selection
   */
  private readonly dataTestAvailabilityConditionDef: Record<DataTestType, Observable<boolean>> = {
    [DataTestType.Column]: of(true),
    // TODO: TECH-7874 Referential integrity remains to be done
    [DataTestType.ReferentialIntegrity]: of(false),
    // can't be more than one NumRowDrift test of each type (one absolute, one relative)
    [DataTestType.NumRowDrift]: this.data$.pipe(
      map((data) => {
        const numRowDriftTestsThresholdTypes = new Set(
          data.dataTests
            .filter((x): x is RowDriftTestForm => x.type === DataTestType.NumRowDrift)
            .map((x) => x.row_change.threshold_type),
        );
        return numRowDriftTestsThresholdTypes.size < 2;
      }),
    ),
    [DataTestType.Unique]: of(true),
    // can't be more than 1 schema test
    [DataTestType.Schema]: this.schemaTestExists$.pipe(map((schemaExists) => !schemaExists)),
    [DataTestType.LowCode]: of(true),
  };

  private readonly dataTestAvailabilityConditionDefAsObservable$ = combineLatest(
    objectKeys(this.dataTestAvailabilityConditionDef).map((key) =>
      this.dataTestAvailabilityConditionDef[key].pipe(map((condition) => ({ type: key, condition }))),
    ),
  );

  /**
   * List of data test types available for adding.
   */
  readonly availableDataTests$: Observable<DataTestType[]> = combineLatest([
    this.dataTestAvailabilityConditionDefAsObservable$,
    this.data$,
  ]).pipe(
    map(([typeAndConditionArray, sidebarData]) => {
      // in edit mode show all tests, to fill the disabled dropdown list with all possible values
      const isEditMode = isEditDataTest(sidebarData);
      return typeAndConditionArray.filter((x) => isEditMode || x.condition).map((x) => x.type);
    }),
  );

  /**
   * All columns from schema data-test
   */
  readonly allSchemaTestColumns$ = this.sideDetailViewService.data$.pipe(
    map((sideBarData) => {
      if (isNullish(sideBarData)) {
        return [];
      }

      const schemaTest = sideBarData.dataTests.find((dt) => dt.type === DataTestType.Schema);
      if (isNullish(schemaTest) || schemaTest.type !== DataTestType.Schema) {
        return [];
      }

      return schemaTest.schema;
    }),
  );

  async runTest(): Promise<void> {
    const dataTestToSave = this.dataTestBuilderFormService.getDataTestToSave();
    if (isNullish(dataTestToSave)) {
      this.errorHandlerService.error('[Data-test] unreachable code. dataTest is null in "runTest()"');
      return;
    }
    await this.dataTestTestRunnerService.runTest(dataTestToSave, this.dataTestDialogStateService.getPath());
  }

  async saveDataTests(): Promise<void> {
    const activeGroup = this.dataTestBuilderFormService.getActiveFormGroup();
    if (!activeGroup.valid) {
      return;
    }

    const sidebarData = await takePromise(this.data$);
    const dataTestToSave = this.dataTestBuilderFormService.getDataTestToSave();

    if (isNullish(dataTestToSave)) {
      console.warn('[Data-test] no data is saved, saving this type is not implemented yet');
      this.sideDetailViewService.close();
      return;
    }

    const dataTests = [...sidebarData.dataTests];

    if (isNewDataTest(sidebarData)) {
      dataTests.push(dataTestToSave);
    } else {
      dataTests[sidebarData.editIndex] = dataTestToSave;
    }

    this.sideDetailViewService.close({ dataTests });
  }

  private disableDataTestTypeDropdownListOnCondition(): void {
    combineLatest([this.data$, this.schemaTestExists$])
      .pipe(
        tap(([sidebarData, schemaTestExists]) => {
          const disableControl = !schemaTestExists || isEditDataTest(sidebarData);
          this.dataTestBuilderFormService.dataTestTypeFormControl[disableControl ? 'disable' : 'enable']({
            emitEvent: false,
          });
        }),
        untilDestroyed(this),
      )
      .subscribe();
  }

  private async getTableColumns(): Promise<DatasourceColumnUIModel[]> {
    const filePath = this.dataTestDialogStateService.getPath();
    if ((filePath ?? '') === '') {
      return [];
    }

    const foundTable = await firstValueFrom(this.datasourceTableQuery.selectUIEntity(filePath));

    if (isNullish(foundTable)) {
      console.error(`[Data-test] no table found in datasource: ${filePath}`);
      return [];
    }

    const result: DatasourceColumnUIModel[] = foundTable.latest_valid_job?.columns ?? [];
    return result;
  }

  private async initializeSchemaTestWithData(): Promise<void> {
    const columns = [...(await this.getTableColumns())]
      .filter((x) => !x.isDeleted)
      .sort((a, b) => a.field_name.localeCompare(b.field_name));

    this.dataTestBuilderFormService.patchDataTestType(DataTestType.Schema);
    this.dataTestBuilderFormService.patchSchemaForm({
      allowNewColumns: false,
      schema: columns.map((column): Required<UnwrapArray<SchemaTestForm['schema']>> => {
        return {
          type: column.data_type,
          column: column.field_name,
          allow_null_values: true,
          required: true,
        };
      }),
    });
  }

  private async initMainFormOnSidebarOpen(): Promise<void> {
    // check if there's no schema test, then create it from the latest job columns
    const schemaTestExists = await firstValueFrom(this.schemaTestExists$);
    if (!schemaTestExists) {
      await this.initializeSchemaTestWithData();
      return;
    }

    // if "add new" command was called
    const data = await takePromise(this.data$);
    if (isNewDataTest(data)) {
      // set data test type dropdown list with first available for dataTest for creating
      const availableDataTests = await takePromise(this.availableDataTests$);
      if (availableDataTests.length > 0) {
        this.dataTestBuilderFormService.patchDataTestType(availableDataTests[0]);
      }
      return;
    }

    const dataTest = data.dataTests[data.editIndex];
    await this.dataTestBuilderFormService.fillMainFormByDataTest(dataTest);
  }
}

function objectKeys<T extends object>(arg: T): Array<keyof T> {
  if (isNullish(arg)) {
    return [];
  }
  return Object.keys(arg) as Array<keyof T>;
}
